import { useState, type FormEvent } from "react";

const FORM_ID = "dahhesjoh653ivfvnvk0";
const FORM_ACTION = "https://readdy.ai/api/form/dahhesjoh653ivfvnvk0";
const MAX_LEN = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trapValue = String(formData.get("company_alt") ?? "").trim();
    if (trapValue) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }

    if (message.length > MAX_LEN) {
      setStatus("error");
      setFormError(`留言内容不能超过 ${MAX_LEN} 个字符，请精简后再提交。`);
      return;
    }

    formData.delete("company_alt");
    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7"
    >
      <h2 className="font-heading font-bold text-xl text-foreground-950">填写联系表单</h2>
      <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项，我们会在两个工作日内回复。</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="用于接收回复"
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
          联系类型 <span className="text-accent-600">*</span>
        </label>
        <select
          id="contact-topic"
          name="topic"
          required
          defaultValue="线索投递"
          className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          <option value="线索投递">线索投递</option>
          <option value="内容更正">内容更正</option>
          <option value="品牌合作">品牌合作</option>
          <option value="订阅推送">订阅推送</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
            具体内容 <span className="text-accent-600">*</span>
          </label>
          <span className={`text-xs ${message.length > MAX_LEN ? "text-accent-600" : "text-foreground-500"}`}>
            {message.length}/{MAX_LEN}
          </span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          maxLength={MAX_LEN}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="请尽量说明信息来源与可核实的关键信息，方便我们快速处理。"
          className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
        ></textarea>
      </div>

      <input
        type="text"
        name="company_alt"
        className="contact-extra-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          <i className="ri-send-plane-line" aria-hidden="true"></i>
          {status === "submitting" ? "提交中..." : "提交表单"}
        </button>
        <p className="text-xs text-foreground-500">
          提交即表示你同意我们仅将该信息用于本次沟通。
        </p>
      </div>

      {status === "success" && (
        <div className="mt-4 p-3.5 rounded-md bg-secondary-50 border border-secondary-200 flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-secondary-700 mt-0.5" aria-hidden="true"></i>
          <p className="text-sm text-secondary-900">提交成功，我们已经收到你的信息，会在两个工作日内回复。</p>
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 p-3.5 rounded-md bg-accent-50 border border-accent-200 flex items-start gap-2">
          <i className="ri-error-warning-line text-accent-700 mt-0.5" aria-hidden="true"></i>
          <p className="text-sm text-accent-900">{formError}</p>
        </div>
      )}
    </form>
  );
}